import * as React from "react"
import {useEffect, useState} from "react"
import {useParams, useNavigate} from "react-router-dom"
import {apiRequestGetQuiz} from "../../api/apiRequests"
import BookingModal from "../member/BookingModal"
import Loader from "../ui/Loader"
import {getSpecialists} from "../../helpers/specialists"

interface Question {
  id: number,
  questionText: string,
  options: string[],
  image: string | null,
  position: number,
}

interface QuizData {
  id: number,
  title: string,
  imageFilename: string | null,
  questions: Question[],
}

const QuizPage = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [quiz, setQuiz] = useState<QuizData | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selected, setSelected] = useState<number | null>(null)
  const [done, setDone] = useState(false)
  const [bookingOpen, setBookingOpen] = useState(false)
  const [randomSpecialist] = useState(() => {
    const specialists = getSpecialists(document.pageInfo?.templateGroup)
    return specialists[Math.floor(Math.random() * specialists.length)]
  })

  useEffect(() => {
    apiRequestGetQuiz(Number(id)).then((data) => {
      if (data && data.id) setQuiz(data)
      setLoading(false)
    })
  }, [id])

  if (loading) return <div className="quiz-page__loader"><Loader/></div>
  if (!quiz) return <div className="quiz-page__error">Quiz not found.</div>

  const questions = quiz.questions
  const current = questions[currentIndex]
  const progress = Math.round((currentIndex / questions.length) * 100)

  const handleSelect = (optionIndex: number) => {
    setSelected(optionIndex)
  }

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      setDone(true)
    } else {
      setCurrentIndex((i) => i + 1)
      setSelected(null)
    }
  }

  if (done) {
    return (
      <section className="quiz-result">
      <div className="container">
        <div className="quiz-result__congrats">
          <div className="quiz-result__icon">🎉</div>
          <h2 className="quiz-result__title">Congratulations!</h2>
          <p className="quiz-result__score">You have completed the quiz! Our expert is ready to help you take the next step.</p>
        </div>
        <div className="quiz-result__manager">
          <img src={randomSpecialist.photo} alt={randomSpecialist.name} className="quiz-result__manager-photo"/>
          <div className="quiz-result__manager-info">
            <p className="quiz-result__manager-name">{randomSpecialist.name}</p>
            <p className="quiz-result__manager-text">Our expert is ready to help you improve your results and reach new goals!</p>
            <button className="quiz-result__schedule-btn" onClick={() => setBookingOpen(true)}>
              Schedule your appointment <span>↗</span>
            </button>
          </div>
        </div>
        <button className="quiz-result__back-btn" onClick={() => navigate("/quizzes")}>
          ← Back to Quizzes
        </button>
        {bookingOpen && (
          <BookingModal specialistName={randomSpecialist.name} onClose={() => setBookingOpen(false)}/>
        )}
      </div>
      </section>
    )
  }

  return (
    <section className="quiz-page">
      <div className="container">
      <div className="quiz-page__header">
        <h2 className="quiz-page__title">{quiz.title}</h2>
      </div>

      <div className="quiz-page__progress-row">
        <span className="quiz-page__progress-label">Question {currentIndex + 1}/{questions.length}</span>
        <div className="quiz-page__progress-bar">
          <div className="quiz-page__progress-fill" style={{width: `${progress}%`}}/>
        </div>
        <span className="quiz-page__progress-pct">{progress}%</span>
      </div>

      <div className="quiz-page__question-wrap">
        <p className="quiz-page__question">{current.questionText}</p>

        {current.image && (
          <img src={current.image} alt="" className="quiz-page__question-image"/>
        )}

        <div className="quiz-page__options">
          {current.options.map((option, i) => (
            <label key={i} className={"quiz-page__option" + (selected === i ? " quiz-page__option--selected" : "")}>
              <input
                type="radio"
                className="quiz-page__option-check"
                checked={selected === i}
                onChange={() => handleSelect(i)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>

        <div className="quiz-page__footer">
          <button
            className="quiz-page__next-btn"
            onClick={handleNext}
            disabled={selected === null}
          >
            {currentIndex + 1 >= questions.length ? "Finish" : "Next >"}
          </button>
        </div>
      </div>
      </div>
    </section>
  )
}

export default QuizPage
